import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { Info } from "lucide-react";

/**
 * Iter 37 — Reusable ⓘ info tooltip for metric labels.
 * Iter 39 — Popup is portaled to <body> and positioned via
 *           getBoundingClientRect so it escapes any per-card transform /
 *           stacking context. Previously the popup got clipped behind
 *           sibling bento tiles because `.bt-bento-tile` uses transform.
 *
 * Behavior:
 *   • Desktop (pointer = mouse): popup appears on hover, dismisses on mouse-leave
 *     (with a short grace period so the cursor can travel into the popup).
 *   • Touch (pointer = touch): popup toggles on tap.
 *   • Any outside click / touch / scroll dismisses a tap-opened popup.
 *
 * Pass `title` + `description` per metric — never hardcode copy here.
 *
 * Props:
 *   - title: short, uppercase heading shown at the top of the popup.
 *   - description: longer body sentence.
 *   - testId: optional data-testid for the trigger button.
 *             The popup body gets `${testId}-popup` for assertions.
 *   - side: "bottom" (default) | "top" — controls vertical popup placement.
 *   - align: "left" (default) | "center" — horizontal anchor relative to the icon.
 */
const POPUP_WIDTH = 256;     // matches w-64
const VIEWPORT_PAD = 8;      // keep at least 8px from any edge
const HOVER_CLOSE_DELAY = 120; // ms — grace period for cursor to reach popup

export default function MetricInfoTooltip({
  title,
  description,
  testId,
  side = "bottom",
  align = "left",
}) {
  const [hoverOpen, setHoverOpen] = useState(false);
  const [clickOpen, setClickOpen] = useState(false);
  const open = hoverOpen || clickOpen;

  const wrapperRef = useRef(null);
  const btnRef = useRef(null);
  const popupRef = useRef(null);
  const closeTimerRef = useRef(null);

  const [pos, setPos] = useState({ top: 0, left: 0 });

  // Recompute the popup's fixed-position coordinates from the trigger button's
  // bounding rect. Called when the popup opens and on scroll/resize.
  const updatePosition = () => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight;
    const popupH = popupRef.current?.offsetHeight || 80;

    // Horizontal anchor
    let left;
    if (align === "center") {
      left = r.left + r.width / 2 - POPUP_WIDTH / 2;
    } else {
      left = r.left;
    }
    // Clamp into viewport
    if (left + POPUP_WIDTH + VIEWPORT_PAD > vw) {
      left = vw - POPUP_WIDTH - VIEWPORT_PAD;
    }
    if (left < VIEWPORT_PAD) left = VIEWPORT_PAD;

    // Vertical placement — honor the explicit `side` prop, but auto-flip
    // if there isn't enough room in that direction.
    const GAP = 8;
    let top;
    const wantTop = side === "top";
    const spaceAbove = r.top;
    const spaceBelow = vh - r.bottom;
    const placeAbove = wantTop
      ? spaceAbove >= popupH + GAP || spaceAbove >= spaceBelow
      : spaceBelow < popupH + GAP && spaceAbove > spaceBelow;
    if (placeAbove) {
      top = r.top - popupH - GAP;
    } else {
      top = r.bottom + GAP;
    }
    // Final clamp so we never spill off-screen
    if (top < VIEWPORT_PAD) top = VIEWPORT_PAD;
    if (top + popupH + VIEWPORT_PAD > vh) top = vh - popupH - VIEWPORT_PAD;

    setPos({ top, left });
  };

  // Position once on open, then re-measure after layout in case popup height
  // changed (e.g. long description wrapped to 3 lines).
  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    const id = requestAnimationFrame(updatePosition);
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Re-position on scroll / resize while open. Capture-phase scroll catches
  // ancestor scrollers too.
  useEffect(() => {
    if (!open) return;
    const handler = () => updatePosition();
    window.addEventListener("scroll", handler, true);
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler, true);
      window.removeEventListener("resize", handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Outside-click / outside-tap dismissal (only when clickOpen is active —
  // hoverOpen self-closes on pointerLeave).
  useEffect(() => {
    if (!clickOpen) return;
    const handler = (e) => {
      const inTrigger = wrapperRef.current && wrapperRef.current.contains(e.target);
      const inPopup = popupRef.current && popupRef.current.contains(e.target);
      if (!inTrigger && !inPopup) setClickOpen(false);
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler, { passive: true });
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [clickOpen]);

  // Cleanup pending grace-period timer on unmount.
  useEffect(() => () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  }, []);

  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => setHoverOpen(false), HOVER_CLOSE_DELAY);
  };

  const handleTriggerEnter = (e) => {
    if (e.pointerType !== "mouse") return;
    cancelClose();
    setHoverOpen(true);
  };
  const handleTriggerLeave = (e) => {
    if (e.pointerType !== "mouse") return;
    scheduleClose();
  };
  const handlePopupEnter = (e) => {
    if (e.pointerType !== "mouse") return;
    cancelClose();
    setHoverOpen(true);
  };
  const handlePopupLeave = (e) => {
    if (e.pointerType !== "mouse") return;
    scheduleClose();
  };

  return (
    <span
      ref={wrapperRef}
      className="relative inline-flex items-center align-middle ml-1"
      onPointerEnter={handleTriggerEnter}
      onPointerLeave={handleTriggerLeave}
    >
      <button
        ref={btnRef}
        type="button"
        aria-label={`Info: ${title}`}
        aria-expanded={open}
        data-testid={testId}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setClickOpen((o) => !o);
        }}
        className="inline-flex items-center justify-center w-4 h-4 rounded-full focus:outline-none focus-visible:ring-1 focus-visible:ring-[var(--bt-accent)] transition-colors"
        style={{ color: "var(--bt-text-muted)" }}
      >
        <Info className="w-3.5 h-3.5" strokeWidth={2} />
      </button>
      {open &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={popupRef}
            role="tooltip"
            data-testid={testId ? `${testId}-popup` : undefined}
            onPointerEnter={handlePopupEnter}
            onPointerLeave={handlePopupLeave}
            className="rounded-md border shadow-lg px-3 py-2.5"
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              width: POPUP_WIDTH,
              maxWidth: `calc(100vw - ${VIEWPORT_PAD * 2}px)`,
              zIndex: 9999,
              background: "var(--bt-surface-2)",
              borderColor: "var(--bt-border)",
              color: "var(--bt-text)",
              pointerEvents: "auto",
            }}
          >
            <div className="text-[11px] font-semibold uppercase tracking-wider">
              {title}
            </div>
            <div
              className="text-xs leading-relaxed mt-1 normal-case font-normal"
              style={{ color: "var(--bt-text-muted)" }}
            >
              {description}
            </div>
          </div>,
          document.body
        )}
    </span>
  );
}
